import React, { useRef, useState, useCallback } from "react";
import DenseCardModal from "./DenseCardModal";
import Calculation from "../Calculation";

const LONG_PRESS_MS = 600;

const DenseCard = ({
    element,
    index,
    handleFinished,
    handleCompleted,
    handleMinus,
    handleCancel,
    handleCookProcess,
    speakOrder,
}) => {
    const [modalOpen, setModalOpen] = useState(false);

    const isStarted = element.currentState === "Started";
    const itemCount = element.OrderItemDetailsList.length;
    const totalQty = element.OrderItemDetailsList.reduce(
        (sum, item) => sum + item.Quantity,
        0
    );

    // ── Press state refs (never cause re-renders) ──────────────────────────────
    const pressTimer = useRef(null);
    const didLongPress = useRef(false);   // did the timer fire before release?
    const startPos = useRef(null);    // track touch start position for scroll detection

    const MOVE_THRESHOLD = 8; // px — more than this = scroll, cancel press

    // ── Start ──────────────────────────────────────────────────────────────────
    const onPressStart = useCallback((clientX, clientY) => {
        // Ignore if already timing (e.g. second touch finger)
        if (pressTimer.current) return;

        didLongPress.current = false;
        startPos.current = { x: clientX, y: clientY };

        pressTimer.current = setTimeout(() => {
            didLongPress.current = true;
            pressTimer.current = null;
            setModalOpen(true);
        }, LONG_PRESS_MS);
    }, []);

    // ── Cancel (scroll / leave / second touch) ────────────────────────────────
    const cancelPress = useCallback(() => {
        if (pressTimer.current) {
            clearTimeout(pressTimer.current);
            pressTimer.current = null;
        }
        startPos.current = null;
    }, []);

    // ── End ───────────────────────────────────────────────────────────────────
    const onPressEnd = useCallback((e) => {
        if (pressTimer.current) {
            // Timer hadn't fired yet → short tap → speak
            clearTimeout(pressTimer.current);
            pressTimer.current = null;

            if (!didLongPress.current) {
                speakOrder(e, element.OrderItemDetailsList, element.tableNum);
            }
        }
        // If didLongPress is true the modal is already open; do nothing else
        startPos.current = null;
    }, [speakOrder, element.OrderItemDetailsList, element.tableNum]);

    // ── Touch move — cancel if the finger is scrolling ────────────────────────
    const onTouchMove = useCallback((e) => {
        if (!startPos.current) return;
        const touch = e.touches[0];
        const dx = Math.abs(touch.clientX - startPos.current.x);
        const dy = Math.abs(touch.clientY - startPos.current.y);
        if (dx > MOVE_THRESHOLD || dy > MOVE_THRESHOLD) {
            cancelPress();
        }
    }, [cancelPress]);

    // ── Normalised handlers ────────────────────────────────────────────────────
    const handlers = {
        onMouseDown: (e) => { if (e.button === 0) onPressStart(e.clientX, e.clientY); },
        onMouseUp: onPressEnd,
        onMouseLeave: cancelPress,          // left the element before release
        onTouchStart: (e) => {
            // Only respond to single-finger touches
            if (e.touches.length === 1) {
                onPressStart(e.touches[0].clientX, e.touches[0].clientY);
            }
        },
        onTouchMove,
        onTouchEnd: (e) => {
            e.preventDefault();             // suppress the mouse-event echo on mobile
            onPressEnd(e);
        },
        onTouchCancel: cancelPress,         // OS interrupted (call, notification, etc.)
    };

    return (
        <>
            {/* Dense tile */}
            <div
                key={index}
                {...handlers}
                className={`
                    select-none
                    rounded-md overflow-hidden
                    border
                    ${isStarted ? "!border-red-500" : "!border-gray-200"}
                    ${isStarted ? "!shadow-red-500/30" : "hover:!border-violet-400"}
                    hover:shadow-md
                    active:scale-[0.97]
                    transition-all duration-150
                    text-[10px]
                    flex flex-col h-full
                `}
            >
                {/* Header strip */}
                <div
                    className={`
                        flex items-center justify-between
                        px-1.5 py-1
                        text-white
                        ${isStarted
                            ? "bg-gradient-to-r from-red-900 via-red-700 to-red-500 pending-header"
                            : "bg-gradient-to-r from-violet-800 via-violet-700 to-purple-600"
                        }
                    `}
                >
                    <span className="font-semibold tracking-wide truncate">
                        K - {element.KOTID}
                    </span>

                    {isStarted && (
                        <span className="
                            bg-yellow-300 text-red-900
                            rounded px-1.5 py-0.5
                            leading-none shrink-0 ml-1
                            font-bold animate-pulse
                        ">
                            NEW
                        </span>
                    )}
                </div>

                {/* Table + timer */}
                <div className="bg-amber-50 border-b border-amber-100 px-1.5 py-1 flex items-center justify-between">
                    <span className="font-bold text-amber-900 truncate tracking-wider text-xs xl:text-sm uppercase">
                        {element.tableNum === "Take-Away"
                            ? "Take-Away"
                            : `Table ${element.tableNum}`}
                    </span>
                    <span className="font-medium shrink-0 ml-1">
                        <Calculation Ordertime={element.orderTime} compact />
                    </span>
                </div>

                {/* Item preview — first 2 items */}
                <div className="flex-1 px-1.5 py-1.5 space-y-1">
                    {element.OrderItemDetailsList.slice(0, 2).map((item, idx) => (
                        <div key={idx} className="flex items-center gap-1 text-gray-700">
                            <span className="w-4 h-4 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center font-bold shrink-0">
                                {item.Quantity}
                            </span>
                            <span className="truncate leading-tight">{item.ItemName}</span>
                        </div>
                    ))}

                    {itemCount > 2 && (
                        <div className="text-gray-400 text-right">
                            +{itemCount - 2} more item{itemCount - 2 > 1 ? "s" : ""}
                        </div>
                    )}
                </div>

                {/* Footer hint */}
                <div className="mt-auto border-t border-gray-100 bg-gray-50 px-1.5 py-1 flex items-center justify-between">
                    <span className="text-gray-400">
                        {totalQty} dish{totalQty !== 1 ? "es" : ""}
                    </span>
                    <span
                        className={`
                            font-medium cursor-pointer
                            transition-all duration-150
                            hover:underline hover:opacity-80 hover:translate-x-0.5
                            active:scale-95 active:opacity-60
                            select-none
                            ${isStarted ? "text-red-500" : "text-violet-500"}
                        `}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                            e.stopPropagation();
                            setModalOpen(true);
                        }}
                    >
                        tap to view →
                    </span>
                </div>
            </div>

            {/* Full detail modal */}
            {modalOpen && (
                <DenseCardModal
                    element={element}
                    onClose={() => setModalOpen(false)}
                    handleFinished={handleFinished}
                    handleCompleted={handleCompleted}
                    handleMinus={handleMinus}
                    handleCancel={handleCancel}
                    handleCookProcess={handleCookProcess}
                />
            )}
        </>
    );
};

export default DenseCard;